/**
 * Un fil qui suit ce qui arrive tant qu'on est en bas : `onScroll` tient `atEnd` (à moins de `slack` px du bas),
 * `followEnd` y redescend. Android s'arrête parfois à l'ancien bas : `followEnd` y revient deux images plus tard
 * (`<ConversationThread>`). Le fil de « À traiter » et l'onglet « Fil » du centre le partagent.
 */
import { useCallback, useEffect, useRef, type RefObject } from "react"
import { type NativeScrollEvent, type NativeSyntheticEvent } from "react-native"

import { type ScrollView } from "../atoms/ScrollView"

export function useFollowEnd(scrollRef: RefObject<ScrollView | null>, slack: number) {
    const atEnd = useRef(true)
    const frame = useRef<number | null>(null)
    const followEnd = useCallback(() => {
        scrollRef.current?.scrollToEnd({ animated: false })
        if (frame.current != null) cancelAnimationFrame(frame.current)
        frame.current = requestAnimationFrame(() => {
            frame.current = requestAnimationFrame(() => {
                frame.current = null
                if (atEnd.current) scrollRef.current?.scrollToEnd({ animated: false })
            })
        })
    }, [scrollRef])
    useEffect(
        () => () => {
            if (frame.current != null) cancelAnimationFrame(frame.current)
        },
        []
    )
    const onScroll = useCallback(
        (event: NativeSyntheticEvent<NativeScrollEvent>) => {
            const { contentOffset, layoutMeasurement, contentSize } = event.nativeEvent
            atEnd.current = contentOffset.y + layoutMeasurement.height >= contentSize.height - slack
        },
        [slack]
    )
    return { atEnd, followEnd, onScroll }
}
